import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Loader2,
  Plus,
  X,
  Sparkles,
  Trophy,
  AlertCircle,
} from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { getPref, setPref } from "../lib/prefs";
import GlossaryText from "../components/GlossaryText";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

const TABS = [
  { key: "wizard", label: "Wizard" },
  { key: "positions", label: "My positions" },
  { key: "scanner", label: "Scanner" },
];

const PHASE_LABELS = {
  put_open: { label: "Put open", color: "#547A8F", bg: "#547A8F15" },
  shares_held: { label: "Shares held", color: "#5C8D73", bg: "#5C8D7315" },
  call_open: { label: "Call open", color: "#D18E63", bg: "#D18E6315" },
  closed: { label: "Closed", color: "#6B7068", bg: "#EAE6E1" },
};

const fmt$ = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => (n == null ? "—" : `${Number(n) >= 0 ? "+" : ""}${Number(n).toFixed(2)}%`);

// CAN SLIM grade → table cell color tokens. Keeps the scanner row free of
// nested ternaries and gives us one place to add new grade tiers.
const CANSLIM_GRADE_STYLE = {
  A: { background: "#5C8D7320", color: "#5C8D73" },
  B: { background: "#547A8F20", color: "#547A8F" },
};
const gradeStyle = (g) => CANSLIM_GRADE_STYLE[g] || { background: "#EAE6E1", color: "#6B7068" };

// =====================================================
// Wizard tab — 3-phase explainer + planner
// =====================================================
function WizardTab() {
  const [symbol, setSymbol] = useState("AAPL");
  const [capital, setCapital] = useState(30000);
  const [otmPct, setOtmPct] = useState(7);
  const [maxDte, setMaxDte] = useState(45);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPlan = async (e) => {
    if (e) e.preventDefault();
    if (!symbol.trim()) return;
    setLoading(true);
    setPlan(null);
    try {
      const r = await api.get(
        `/wheel/plan/${symbol.trim().toUpperCase()}?capital=${capital}&target_otm_pct=${otmPct}&max_dte=${maxDte}`
      );
      setPlan(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No options data for this symbol");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      {/* PHASE EXPLAINER */}
      <section>
        <h2 className="font-heading text-2xl text-[#2A2F2B] mb-4">How the Wheel works</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              n: 1,
              title: "Sell a cash-secured put",
              color: "#547A8F",
              bg: "#547A8F15",
              body:
                "Pick a stock you'd be happy to own at a lower price. Sell a put at that strike, collect premium. If it expires worthless → keep the cash. If it gets assigned → you buy the shares at the strike (cost basis = strike − premium).",
            },
            {
              n: 2,
              title: "Hold the assigned shares",
              color: "#5C8D73",
              bg: "#5C8D7315",
              body:
                "You now own 100 shares per contract at a discount. The premium you collected reduces your effective cost basis. While holding, decide what strike you'd be happy to sell at.",
            },
            {
              n: 3,
              title: "Sell covered calls",
              color: "#D18E63",
              bg: "#D18E6315",
              body:
                "Sell calls at or above your cost basis to collect more premium. If they expire OTM → keep the cash, sell another. If called away → realize the gain plus all premium collected, and start the wheel over again.",
            },
          ].map((p) => (
            <div
              key={p.n}
              data-testid={`wheel-phase-${p.n}`}
              className="rounded-2xl bg-white border border-[#EAE6E1] p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center font-mono-ticker font-bold text-sm"
                  style={{ background: p.bg, color: p.color }}
                >
                  {p.n}
                </div>
                <h3 className="font-heading text-base text-[#2A2F2B]">{p.title}</h3>
              </div>
              <GlossaryText text={p.body} className="text-sm text-[#6B7068] leading-relaxed" />
            </div>
          ))}
        </div>
      </section>

      {/* PLANNER */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl text-[#2A2F2B]">Wheel planner</h2>
            <p className="text-xs text-[#6B7068] mt-1">
              Pick a ticker + your available capital. We'll find the best cash-secured-put leg.
            </p>
          </div>
        </div>
        <form
          onSubmit={runPlan}
          className="grid grid-cols-2 md:grid-cols-5 gap-3 rounded-2xl bg-white border border-[#EAE6E1] p-4 mb-5"
          data-testid="wheel-planner-form"
        >
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Ticker</Label>
            <Input
              data-testid="wheel-planner-symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="AAPL"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Capital ($)</Label>
            <Input
              data-testid="wheel-planner-capital"
              type="number"
              value={capital}
              min={1000}
              step={500}
              onChange={(e) => setCapital(Number(e.target.value))}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Target OTM %</Label>
            <Input
              data-testid="wheel-planner-otm"
              type="number"
              value={otmPct}
              min={1}
              max={30}
              step={1}
              onChange={(e) => setOtmPct(Number(e.target.value))}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Max DTE (days)</Label>
            <Input
              data-testid="wheel-planner-dte"
              type="number"
              value={maxDte}
              min={5}
              max={120}
              step={5}
              onChange={(e) => setMaxDte(Number(e.target.value))}
              className="mt-1"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              data-testid="wheel-planner-run"
              disabled={loading}
              className="w-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find legs"}
            </Button>
          </div>
        </form>

        {plan && (
          <div data-testid="wheel-plan-results">
            <div className="flex items-center justify-between text-sm text-[#6B7068] mb-3">
              <div>
                <span className="font-mono-ticker text-[#2A2F2B] font-semibold">{plan.symbol}</span> @ {fmt$(plan.current_price)} ·
                target strike ≈ {fmt$(plan.target_strike)} ({plan.target_otm_pct}% OTM)
              </div>
              <span className="text-xs">{plan.evaluated_expiries} expiries scanned</span>
            </div>
            {plan.candidates.length === 0 ? (
              <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 text-center" data-testid="wheel-plan-empty">
                <AlertCircle className="w-10 h-10 text-[#6B7068] mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-[#6B7068]">
                  No suitable put legs found. Try increasing capital, widening DTE, or lowering target OTM%.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {plan.candidates.map((c, idx) => (
                  <PlannerLegCard key={`${c.expiry}-${c.strike}`} leg={c} symbol={plan.symbol} idx={idx} />
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function PlannerLegCard({ leg, symbol, idx }) {
  const [opening, setOpening] = useState(false);
  const open = async () => {
    setOpening(true);
    try {
      await api.post("/wheel/positions", {
        symbol,
        strike: leg.strike,
        expiry: leg.expiry,
        premium: leg.premium,
        contracts: 1,
      });
      toast.success(`Wheel started — sold ${symbol} $${leg.strike} put for $${leg.premium.toFixed(2)} credit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open position");
    } finally {
      setOpening(false);
    }
  };
  const isTop = idx === 0;
  return (
    <div
      data-testid={`wheel-leg-${leg.expiry}-${leg.strike}`}
      className={`rounded-2xl bg-white border p-4 md:p-5 grid grid-cols-2 md:grid-cols-7 gap-3 items-center transition-soft ${
        isTop ? "border-[#5C8D73]" : "border-[#EAE6E1]"
      }`}
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Expiry · DTE</div>
        <div className="font-mono-ticker text-sm font-semibold text-[#2A2F2B]">{leg.expiry}</div>
        <div className="font-mono-ticker text-[11px] text-[#6B7068]">{leg.dte} days</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Strike</div>
        <div className="font-mono-ticker text-sm font-semibold text-[#2A2F2B]">{fmt$(leg.strike)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Premium</div>
        <div className="font-mono-ticker text-sm font-semibold text-[#2A2F2B]">${leg.premium.toFixed(2)}</div>
        <div className="font-mono-ticker text-[11px] text-[#6B7068]">${leg.premium_total.toFixed(0)} total</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Capital req.</div>
        <div className="font-mono-ticker text-sm font-semibold text-[#2A2F2B]">{fmt$(leg.capital_required)}</div>
        <div className="font-mono-ticker text-[11px] text-[#6B7068]">BE {fmt$(leg.breakeven)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Annualized</div>
        <div className="font-mono-ticker text-sm font-semibold" style={{ color: "#5C8D73" }}>
          {leg.annualized_pct.toFixed(1)}%
        </div>
        <div className="font-mono-ticker text-[11px] text-[#6B7068]">period {leg.return_pct.toFixed(2)}%</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Score</div>
        <div className="flex items-center gap-1">
          <span className="font-mono-ticker text-sm font-semibold text-[#2A2F2B]">{leg.score}</span>
          {isTop && <Trophy className="w-3.5 h-3.5 text-[#D18E63]" strokeWidth={2} />}
        </div>
        <div className="font-mono-ticker text-[10px] text-[#6B7068]">OI {leg.open_interest}</div>
      </div>
      <div className="col-span-2 md:col-span-1 flex md:justify-end">
        <Button
          onClick={open}
          disabled={opening}
          data-testid={`wheel-leg-open-${leg.expiry}-${leg.strike}`}
          className="bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-xs"
        >
          {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
            <>
              <Plus className="w-3.5 h-3.5 mr-1" /> Open
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// =====================================================
// Positions tab
// =====================================================
function PositionsTab() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeClosed, setIncludeClosed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/wheel/positions?include_closed=${includeClosed}`);
      setPositions(r.data.positions || []);
    } catch (e) {
      toast.error("Failed to load positions");
    } finally {
      setLoading(false);
    }
  }, [includeClosed]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 flex items-center gap-2 text-sm text-[#6B7068]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading positions...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-[#2A2F2B]">My Wheel positions</h2>
        <label className="flex items-center gap-2 text-xs text-[#6B7068]">
          <input
            type="checkbox"
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
            data-testid="wheel-include-closed"
          />
          Show closed
        </label>
      </div>
      {positions.length === 0 ? (
        <div className="rounded-2xl bg-white border border-[#EAE6E1] p-10 text-center" data-testid="wheel-positions-empty">
          <RefreshCw className="w-10 h-10 text-[#6B7068] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-heading text-lg text-[#2A2F2B] mb-1">No Wheel positions yet</h3>
          <p className="text-sm text-[#6B7068]">
            Open your first cash-secured put from the Wizard tab to start tracking the Wheel.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="wheel-positions-list">
          {positions.map((p) => (
            <PositionCard key={p.id} pos={p} reload={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function PositionCard({ pos, reload }) {
  const phase = PHASE_LABELS[pos.phase] || PHASE_LABELS.closed;
  const [busy, setBusy] = useState(false);
  const [callOpen, setCallOpen] = useState(false);

  const transition = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/wheel/positions/${pos.id}/transition`, { action });
      toast.success(`Position transitioned: ${action.replace(/_/g, " ")}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Delete this Wheel position for ${pos.symbol}?`)) return;
    try {
      await api.delete(`/wheel/positions/${pos.id}`);
      toast.success("Position deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div
      data-testid={`wheel-position-${pos.id}`}
      className="rounded-2xl bg-white border border-[#EAE6E1] p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to={`/asset/stock/${pos.symbol}`}
            className="font-mono-ticker text-lg font-bold text-[#2A2F2B] hover:text-[#547A8F]"
          >
            {pos.symbol}
          </Link>
          <span
            className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: phase.bg, color: phase.color }}
            data-testid={`wheel-phase-badge-${pos.id}`}
          >
            {phase.label}
          </span>
        </div>
        <button
          onClick={remove}
          className="text-[#6B7068] hover:text-[#C66B59] transition-soft"
          data-testid={`wheel-position-delete-${pos.id}`}
          aria-label="Delete position"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        {pos.leg && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">
              {pos.leg.type === "put" ? "Short put" : "Short call"}
            </div>
            <div className="font-mono-ticker text-sm text-[#2A2F2B]">
              ${pos.leg.strike}{" "}
              <span className="text-[#6B7068]">exp {pos.leg.expiry}</span>
            </div>
            <div className="font-mono-ticker text-[11px] text-[#6B7068]">
              {pos.leg.contracts}× @ ${pos.leg.premium.toFixed(2)}
            </div>
          </div>
        )}
        {pos.cost_basis != null && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Cost basis</div>
            <div className="font-mono-ticker text-sm text-[#2A2F2B]">{fmt$(pos.cost_basis)}</div>
            <div className="font-mono-ticker text-[11px] text-[#6B7068]">{pos.shares} shares</div>
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Premium collected</div>
          <div className="font-mono-ticker text-sm font-semibold" style={{ color: "#5C8D73" }}>
            +{fmt$(pos.premium_collected)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Realized P/L</div>
          <div
            className="font-mono-ticker text-sm font-semibold"
            style={{ color: pos.realized_pnl >= 0 ? "#5C8D73" : "#C66B59" }}
          >
            {pos.realized_pnl >= 0 ? "+" : ""}
            {fmt$(pos.realized_pnl)}
          </div>
        </div>
      </div>

      {/* Action buttons depending on phase */}
      <div className="mt-4 flex flex-wrap gap-2">
        {pos.phase === "put_open" && (
          <>
            <Button
              size="sm"
              onClick={() => transition("expired_otm")}
              disabled={busy}
              data-testid={`wheel-action-expired-${pos.id}`}
              className="bg-[#5C8D73] hover:bg-[#4a7560] text-white text-xs"
            >
              Expired OTM (kept premium)
            </Button>
            <Button
              size="sm"
              onClick={() => transition("assigned")}
              disabled={busy}
              data-testid={`wheel-action-assigned-${pos.id}`}
              className="bg-[#547A8F] hover:bg-[#426071] text-white text-xs"
            >
              Assigned → hold shares
            </Button>
          </>
        )}
        {pos.phase === "shares_held" && (
          <Button
            size="sm"
            onClick={() => setCallOpen(true)}
            data-testid={`wheel-action-sell-call-${pos.id}`}
            className="bg-[#D18E63] hover:bg-[#b8784f] text-white text-xs"
          >
            <Plus className="w-3 h-3 mr-1" /> Sell covered call
          </Button>
        )}
        {pos.phase === "call_open" && (
          <>
            <Button
              size="sm"
              onClick={() => transition("call_expired_otm")}
              disabled={busy}
              data-testid={`wheel-action-call-expired-${pos.id}`}
              className="bg-[#5C8D73] hover:bg-[#4a7560] text-white text-xs"
            >
              Call expired OTM
            </Button>
            <Button
              size="sm"
              onClick={() => transition("called_away")}
              disabled={busy}
              data-testid={`wheel-action-called-${pos.id}`}
              className="bg-[#547A8F] hover:bg-[#426071] text-white text-xs"
            >
              Called away → close
            </Button>
          </>
        )}
        {pos.phase !== "closed" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => transition("manual_close")}
            disabled={busy}
            className="text-xs"
          >
            Manual close
          </Button>
        )}
      </div>

      <SellCallDialog open={callOpen} onOpenChange={setCallOpen} pos={pos} reload={reload} />
    </div>
  );
}

function SellCallDialog({ open, onOpenChange, pos, reload }) {
  const [strike, setStrike] = useState("");
  const [expiry, setExpiry] = useState("");
  const [premium, setPremium] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!open || !pos) return;
    setStrike("");
    setExpiry("");
    setPremium("");
    api
      .get(`/wheel/cc-plan/${pos.symbol}?cost_basis=${pos.cost_basis}&shares=${pos.shares}&limit=4`)
      .then((r) => setSuggestions(r.data.candidates || []))
      .catch(() => setSuggestions([]));
  }, [open, pos]);

  const submit = async () => {
    if (!strike || !expiry || !premium) {
      toast.error("All fields required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/wheel/positions/${pos.id}/sell-call`, {
        strike: parseFloat(strike),
        expiry,
        premium: parseFloat(premium),
        contracts: pos.shares / 100,
      });
      toast.success(`Covered call opened: $${strike} exp ${expiry}`);
      onOpenChange(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open call");
    } finally {
      setSubmitting(false);
    }
  };

  if (!pos) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sell-call-dialog">
        <DialogHeader>
          <DialogTitle>Sell covered call on {pos.symbol}</DialogTitle>
          <DialogDescription>
            Cost basis ${pos.cost_basis} · {pos.shares} shares ({pos.shares / 100} contracts available).
            Suggestions below avoid strikes that would lock in a loss.
          </DialogDescription>
        </DialogHeader>
        {suggestions.length > 0 && (
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">
              <Sparkles className="w-3 h-3 inline mr-1 text-[#D18E63]" /> Smart strikes
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5 mb-3">
              {suggestions.map((s) => (
                <button
                  key={`${s.expiry}-${s.strike}`}
                  type="button"
                  onClick={() => {
                    setStrike(String(s.strike));
                    setExpiry(s.expiry);
                    setPremium(String(s.premium));
                  }}
                  data-testid={`cc-suggestion-${s.expiry}-${s.strike}`}
                  className="px-2.5 py-1 text-[11px] rounded-full bg-[#F9F8F6] hover:bg-[#EAE6E1] text-[#2A2F2B] border border-[#EAE6E1] transition-soft"
                >
                  ${s.strike} · {s.expiry} · ${s.premium.toFixed(2)} ({s.if_called_pct.toFixed(1)}%)
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label>Strike ($)</Label>
            <Input value={strike} onChange={(e) => setStrike(e.target.value)} placeholder="e.g. 300" data-testid="cc-strike-input" />
          </div>
          <div>
            <Label>Expiry (YYYY-MM-DD)</Label>
            <Input value={expiry} onChange={(e) => setExpiry(e.target.value)} placeholder="2026-06-21" data-testid="cc-expiry-input" />
          </div>
          <div>
            <Label>Premium per share ($)</Label>
            <Input value={premium} onChange={(e) => setPremium(e.target.value)} placeholder="e.g. 3.20" data-testid="cc-premium-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="cc-submit" className="bg-[#2A2F2B] hover:bg-[#547A8F] text-white">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open covered call"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================
// Scanner tab
// =====================================================
function ScannerTab() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capital, setCapital] = useState(25000);
  const [otmPct, setOtmPct] = useState(7);
  const [maxDte, setMaxDte] = useState(45);
  const [meta, setMeta] = useState({});

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/wheel/scanner?capital=${capital}&target_otm_pct=${otmPct}&max_dte=${maxDte}&limit=10`
      );
      setItems(r.data.items || []);
      setMeta({ evaluated: r.data.evaluated, found: r.data.found, cached: r.data.cached });
    } catch (e) {
      toast.error("Scanner failed");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [capital, otmPct, maxDte]);

  // Auto-run once on mount; users tweak filters then hit "Run" for re-runs to
  // avoid spamming the (slow, finviz-backed) scanner on every keystroke.
  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl text-[#2A2F2B]">Wheel scanner</h2>
          <p className="text-xs text-[#6B7068] mt-1">
            Top {items?.length || 0} cash-secured puts across your watchlist + the curated Wheel universe.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Capital</Label>
            <Input
              type="number"
              value={capital}
              onChange={(e) => setCapital(Number(e.target.value))}
              className="w-28 mt-1"
              data-testid="scanner-capital"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">OTM %</Label>
            <Input
              type="number"
              value={otmPct}
              onChange={(e) => setOtmPct(Number(e.target.value))}
              className="w-20 mt-1"
              data-testid="scanner-otm"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Max DTE</Label>
            <Input
              type="number"
              value={maxDte}
              onChange={(e) => setMaxDte(Number(e.target.value))}
              className="w-20 mt-1"
              data-testid="scanner-dte"
            />
          </div>
          <Button onClick={run} disabled={loading} data-testid="scanner-run" className="bg-[#2A2F2B] hover:bg-[#547A8F] text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run scan"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white border border-[#EAE6E1] p-10 text-center text-sm text-[#6B7068]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> Scanning {meta.evaluated || 50}+ tickers...
        </div>
      ) : items && items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-[#EAE6E1] p-10 text-center text-sm text-[#6B7068]" data-testid="scanner-empty">
          <AlertCircle className="w-10 h-10 mx-auto mb-3" strokeWidth={1.5} /> No leg matched — try increasing capital or DTE.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl bg-white border border-[#EAE6E1]" data-testid="scanner-results">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6] border-b border-[#EAE6E1]">
              <tr className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">
                <th className="text-left px-4 py-3">Ticker</th>
                <th className="text-right px-3">Price</th>
                <th className="text-right px-3">Strike</th>
                <th className="text-right px-3">Exp · DTE</th>
                <th className="text-right px-3">Premium</th>
                <th className="text-right px-3">Annualized</th>
                <th className="text-right px-3">CAN SLIM</th>
                <th className="text-right px-4">Score</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.symbol} className="border-b last:border-0 border-[#EAE6E1] hover:bg-[#F9F8F6]/50 transition-soft" data-testid={`scanner-row-${it.symbol}`}>
                  <td className="px-4 py-3">
                    <Link to={`/asset/stock/${it.symbol}`} className="font-mono-ticker font-semibold text-[#2A2F2B] hover:text-[#547A8F]">
                      {it.symbol}
                    </Link>
                  </td>
                  <td className="text-right px-3 font-mono-ticker text-[#2A2F2B]">{fmt$(it.current_price)}</td>
                  <td className="text-right px-3 font-mono-ticker text-[#2A2F2B]">{fmt$(it.strike)}</td>
                  <td className="text-right px-3 font-mono-ticker text-[#6B7068] text-[11px]">
                    {it.expiry}<br />
                    <span className="text-[10px]">{it.dte}d</span>
                  </td>
                  <td className="text-right px-3 font-mono-ticker text-[#2A2F2B]">${it.premium.toFixed(2)}</td>
                  <td className="text-right px-3 font-mono-ticker font-semibold" style={{ color: "#5C8D73" }}>
                    {it.annualized_pct.toFixed(1)}%
                  </td>
                  <td className="text-right px-3">
                    {it.canslim_grade ? (
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full font-mono-ticker font-bold text-xs"
                        style={gradeStyle(it.canslim_grade)}
                      >
                        {it.canslim_grade}
                      </span>
                    ) : (
                      <span className="text-[#6B7068] text-xs">—</span>
                    )}
                  </td>
                  <td className="text-right px-4 font-mono-ticker font-bold text-[#2A2F2B]">{it.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =====================================================
// Page shell
// =====================================================
export default function WheelPage() {
  const [tab, setTab] = useState(() => getPref("wheel_tab") || "wizard");

  useEffect(() => {
    setPref("wheel_tab", tab);
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-10 md:py-14" data-testid="wheel-page">
        <header className="mb-8 animate-fade-up">
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
            Income strategy
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B] mt-1">
            The Wheel
          </h1>
          <p className="text-[#6B7068] mt-2 max-w-2xl">
            <GlossaryText
              text="A 3-phase options strategy that pairs cash-secured puts with covered calls to generate steady premium income on stocks you'd be happy to own. UpTick walks you through the mechanics, paper-trades the lifecycle, and scans for the best legs."
            />
          </p>
        </header>

        <div className="inline-flex items-center bg-white border border-[#EAE6E1] rounded-full p-1 gap-0.5 mb-8" data-testid="wheel-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`wheel-tab-${t.key}`}
              className={`px-5 py-2 text-xs font-medium uppercase tracking-[0.18em] rounded-full transition-soft ${
                tab === t.key
                  ? "bg-[#2A2F2B] text-white shadow-sm"
                  : "text-[#6B7068] hover:text-[#2A2F2B]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "wizard" && <WizardTab />}
        {tab === "positions" && <PositionsTab />}
        {tab === "scanner" && <ScannerTab />}
      </main>
    </div>
  );
}
